import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { loadAllUsers, updateUserRole, deleteUser } from '../lib/db'

const ROLE_LABELS = { owner: 'Owner', road_super: 'Road Super', admin: 'Admin', pm: 'PM', foreman: 'Foreman' }
const ROLE_COLORS = {
  owner: { bg: '#E6F1FB', color: '#0C447C' },
  road_super: { bg: '#EEE6FB', color: '#5B2D9E' },
  admin: { bg: '#E2F4F1', color: '#0F6E60' },
  pm: { bg: '#EAF3DE', color: '#3B6D11' },
  foreman: { bg: '#FAEEDA', color: '#854F0B' },
}



const CREATE_ERR = {
  invalid_email: 'That email address doesn’t look right.',
  invalid_role: 'Pick a role.',
  foreman_needs_employee: 'A foreman login has to be linked to their employee record — that link is what decides which field reports they see.',
  employee_already_linked: 'That employee is already linked to another login.',
  auth_create_failed: 'Could not create the login.',
  db_error: 'Something went wrong. Please try again.',
}

async function authedFetch(url, opts = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  return fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}), Authorization: `Bearer ${session?.access_token}` },
  })
}

// Create a login for someone — typically a foreman, who must be linked to their
// employees row (that link is what scopes their Field Reports view; see
// supabase-app-users-employee-id.sql). Replaces the old "do it by hand in the
// Supabase dashboard, then run this SQL" instructions.
function AddLoginModal({ onClose, onCreated }) {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [empId, setEmpId] = useState('')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('foreman')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(null)   // { email, temp_password, existing_login }
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authedFetch('/api/app-users?action=options')
        const data = await r.json()
        if (cancelled) return
        if (r.ok && data.ok) setEmployees(data.employees || [])
        else setErr('Could not load the employee list.')
      } catch (e) { if (!cancelled) setErr('Could not load the employee list.') }
      finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  // Picking an employee pre-fills their name + notification email; both stay
  // editable (Jorge Rodrigues signs in with a personal address, for one).
  const pickEmployee = (id) => {
    setEmpId(id); setErr(null)
    const e = employees.find(x => String(x.id) === String(id))
    if (e) { setName(e.name || ''); if (e.email) setEmail(e.email) }
  }

  async function create() {
    setBusy(true); setErr(null)
    try {
      const r = await authedFetch('/api/app-users?action=create', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), name: name.trim(), role, employee_id: empId ? Number(empId) : null }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) { setErr(CREATE_ERR[data.error] || data.message || CREATE_ERR.db_error); return }
      setDone({ email: email.trim(), temp_password: data.temp_password, existing_login: data.existing_login })
      onCreated()
    } catch (e) {
      setErr(CREATE_ERR.db_error)
    } finally { setBusy(false) }
  }

  const canSubmit = !!email.trim() && !!role && !busy && (role !== 'foreman' || !!empId)

  if (done) return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Login created</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          <strong>{done.email}</strong> can now sign in at cornerstonecrewdispatch.com.
        </div>
        {done.temp_password ? (
          <>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Temporary password — give this to them now, it isn’t shown again:</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
              <code style={{ flex: 1, background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 15, letterSpacing: 0.5 }}>{done.temp_password}</code>
              <button className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(done.temp_password); setCopied(true) }}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              They can change it any time with “Forgot password” on the sign-in screen.
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--text3)', marginBottom: 16 }}>
            This email already had a login, so its existing password was left alone. If they don’t know it, have them use <strong>Forgot password</strong> on the sign-in screen.
          </div>
        )}
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Add a login</div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading employees…</div>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-select" value={role} onChange={e => { setRole(e.target.value); setErr(null) }}>
                <option value="foreman">Foreman — their own field reports only</option>
                <option value="pm">PM — reports for their jobs</option>
                <option value="admin">Admin — office staff</option>
                <option value="road_super">Road Super</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Employee record{role === 'foreman' ? '' : ' (optional)'}</label>
              <select className="form-select" value={empId} onChange={e => pickEmployee(e.target.value)}>
                <option value="">— none (office login) —</option>
                {employees.map(e => (
                  <option key={e.id} value={e.id} disabled={!!e.linked_to}>
                    {e.name}{e.is_foreman ? ' ★' : ''}{e.linked_to ? ' — already has a login' : ''}
                  </option>
                ))}
              </select>
              {role === 'foreman' && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  Required for a foreman — this is what decides which reports they see.
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Sign-in email</label>
              <input className="form-input" type="email" value={email} onChange={e => { setEmail(e.target.value); setErr(null) }} placeholder="name@cornerstonecontracting.net" />
            </div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
            </div>
            {err && <div style={{ fontSize: 12.5, color: 'var(--red-text)', marginBottom: 12 }}>{err}</div>}
          </>
        )}
        <div className="modal-actions">
          <button className="btn btn-primary" disabled={!canSubmit} onClick={create}>{busy ? 'Creating…' : 'Create login'}</button>
          <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function ConfirmDeleteModal({ user, onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 340 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Remove user?</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 18 }}>
          Remove <strong>{user.name || user.email}</strong> from Crew Dispatch? They will no longer be able to log in.
        </div>
        <div className="modal-actions">
          <button className="btn btn-danger" onClick={onConfirm}>Remove</button>
          <button className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

export default function UserManagement({ currentUserId }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [employees, setEmployees] = useState([])   // for the linked-employee column

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const data = await loadAllUsers()
      console.log('UserManagement fetchUsers result:', data, 'length:', data?.length)
      setUsers(data || [])
    } catch(e) {
      console.error('UserManagement fetchUsers error:', e)
    }
    setLoading(false)
  }

  const fetchEmployees = async () => {
    try {
      const r = await authedFetch('/api/app-users?action=options')
      const data = await r.json()
      if (r.ok && data.ok) setEmployees(data.employees || [])
    } catch (e) { /* the linked-employee column just shows the raw id */ }
  }

  useEffect(() => { fetchUsers(); fetchEmployees() }, [])

  const empName = (id) => employees.find(e => e.id === id)?.name || (id != null ? `Employee #${id}` : null)

  // Link an existing login to an employee record. Only meaningful for foremen —
  // it is what their field-report visibility is resolved through.
  const handleLinkChange = async (userId, raw) => {
    const employee_id = raw === '' ? null : Number(raw)
    const prev = users
    setUsers(us => us.map(u => u.id === userId ? { ...u, employee_id } : u))
    const r = await authedFetch('/api/app-users?action=link', {
      method: 'POST', body: JSON.stringify({ user_id: userId, employee_id }),
    })
    if (!r.ok) { setUsers(prev); await fetchUsers() }
    else await fetchEmployees()
  }

  const handleRoleChange = async (userId, newRole) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u))
    await updateUserRole(userId, newRole)
  }

  const handleDelete = async (user) => {
    setConfirmDelete(null)
    await deleteUser(user.id)
    setUsers(prev => prev.filter(u => u.id !== user.id))
  }

  const roleOrder = { owner: 0, road_super: 1, admin: 2, pm: 3, foreman: 4 }
  const sorted = [...users].sort((a, b) => roleOrder[a.role] - roleOrder[b.role])

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>App users</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{users.length} user{users.length !== 1 ? 's' : ''} have access to Crew Dispatch</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add login</button>
      </div>

      {/* Role legend */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {Object.entries(ROLE_LABELS).map(([role, label]) => (
          <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text2)' }}>
            <span style={{ ...ROLE_COLORS[role], borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 500 }}>{label}</span>
            <span>{role === 'owner' ? 'Full access + users' : role === 'road_super' ? 'Full access, no users tab' : role === 'admin' ? 'Office staff — field reports; approve escalated' : role === 'pm' ? 'View board, make requests' : 'View only, no requests'}</span>
          </div>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading users...</div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No users yet. Click + Invite user to add someone.</div>
        ) : sorted.map((user, i) => (
          <div key={user.id} className="emp-row" style={{ borderBottom: i < sorted.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
            {/* Avatar */}
            <div className="avatar" style={{ background: ROLE_COLORS[user.role]?.bg, color: ROLE_COLORS[user.role]?.color, fontSize: 11, fontWeight: 600 }}>
              {(user.name || user.email).slice(0, 2).toUpperCase()}
            </div>
            {/* Info */}
            <div className="emp-info">
              <div className="emp-name">{user.name || '—'}
                {user.id === currentUserId && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--surface2)', color: 'var(--text3)', borderRadius: 4, padding: '1px 5px', border: '0.5px solid var(--border)' }}>you</span>}
              </div>
              <div className="emp-meta">{user.email}</div>
              {/* The employee link only drives foreman report visibility, so it's
                  only surfaced (and only warned about) for foremen. */}
              {user.role === 'foreman' && (
                user.employee_id != null ? (
                  <div className="emp-meta" style={{ color: 'var(--text3)' }}>
                    Linked to {empName(user.employee_id)}
                    <button className="btn btn-sm" style={{ marginLeft: 6, padding: '0 6px', fontSize: 10 }}
                      onClick={() => handleLinkChange(user.id, '')}>unlink</button>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--red-text)', marginTop: 2 }}>
                    ⚠ Not linked to an employee — they’ll see no field reports.{' '}
                    <select className="form-select" style={{ fontSize: 11, width: 170, marginTop: 3 }}
                      value="" onChange={e => e.target.value && handleLinkChange(user.id, e.target.value)}>
                      <option value="">Link to employee…</option>
                      {employees.filter(e => !e.linked_to).map(e => (
                        <option key={e.id} value={e.id}>{e.name}{e.is_foreman ? ' ★' : ''}</option>
                      ))}
                    </select>
                  </div>
                )
              )}
            </div>
            {/* Role selector */}
            <select
              className="form-select"
              value={user.role}
              onChange={e => handleRoleChange(user.id, e.target.value)}
              disabled={user.id === currentUserId}
              style={{ width: 130, fontSize: 12, marginLeft: 8,
                background: ROLE_COLORS[user.role]?.bg,
                color: ROLE_COLORS[user.role]?.color,
                border: 'none', fontWeight: 500 }}>
              <option value="owner">Owner</option>
              <option value="road_super">Road Super</option>
              <option value="admin">Admin</option>
              <option value="pm">PM</option>
              <option value="foreman">Foreman</option>
            </select>
            {/* Remove */}
            {user.id !== currentUserId && (
              <button className="btn btn-sm" style={{ color: 'var(--red-text)', marginLeft: 8, flexShrink: 0 }}
                onClick={() => setConfirmDelete(user)}>Remove</button>
            )}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>
        💡 <strong>+ Add login</strong> creates the account and hands you a one-time password to pass along — no Supabase steps. A foreman must be linked to their employee record; that link is what decides which field reports they can see. Forgotten passwords are reset by the user with “Forgot password” on the sign-in screen.
      </div>

      {showAdd && <AddLoginModal onClose={() => setShowAdd(false)} onCreated={() => { fetchUsers(); fetchEmployees() }} />}
      {confirmDelete && <ConfirmDeleteModal user={confirmDelete} onConfirm={() => handleDelete(confirmDelete)} onCancel={() => setConfirmDelete(null)} />}
    </div>
  )
}
